'use client'

import { ChevronRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { findBrandBySlug, useGetCityBrandsQuery } from '@/api/brands/get-brand-query'
import { useGetRestaurantsQuery } from '@/api/restaurants/get-restaurants-query'
import { Middot } from '@/components/middot'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { Rating } from '@/features/reviews/rating'
import { Locale } from '@/setup/locales'
import { getLinkHref } from '@/setup/routing'
import { haversineDistanceKm } from '@/utils/functions/distance'
import { createImageUrl } from '@/utils/image/create-image-url'

const MAX_SIBLING_LINKS = 6

export const RestaurantDetailBrandLinks = () => {
    const { restaurant, citySlug } = useRestaurantDetailContext()
    const t = useTranslations()
    const locale = useLocale() as Locale

    const brandSlug = restaurant?.brandSlug
    const resolvedCitySlug = restaurant?.city?.slug ?? citySlug

    const { data: brandsData } = useGetCityBrandsQuery(resolvedCitySlug ?? '')
    const { data } = useGetRestaurantsQuery({
        citySlug: resolvedCitySlug,
        brandSlug,
    })

    const brand = findBrandBySlug(brandsData?.data, brandSlug)
    const cityName = restaurant?.city?.name

    if (!restaurant || !brandSlug || !brand || !resolvedCitySlug || !cityName) return null

    const siblings = (data?.data ?? []).filter((sibling) => sibling.slug !== restaurant.slug)

    if (!siblings.length) return null

    // Nearest alternatives first: sorted by proximity to the restaurant on this
    // page, since that is the location the visitor is currently considering
    const sortedSiblings =
        typeof restaurant.latitude === 'number' && typeof restaurant.longitude === 'number'
            ? [...siblings].sort(
                  (a, b) =>
                      haversineDistanceKm(
                          { lat: restaurant.latitude ?? 0, lng: restaurant.longitude ?? 0 },
                          { lat: a.latitude, lng: a.longitude }
                      ) -
                      haversineDistanceKm(
                          { lat: restaurant.latitude ?? 0, lng: restaurant.longitude ?? 0 },
                          { lat: b.latitude, lng: b.longitude }
                      )
              )
            : siblings

    const brandHref = getLinkHref(
        {
            pattern: '/restaurants/[citySlug]/brands/[brandSlug]',
            params: { citySlug: resolvedCitySlug, brandSlug },
        },
        locale
    )

    return (
        <section
            className="flex flex-col w-full pb-10 md:pb-14 mb-10 md:mb-14 border-b-[1px]
                border-b-border-primary"
        >
            <div className="flex items-center gap-x-3 mb-5">
                {brand.logoUrl && (
                    <Image
                        unoptimized
                        src={createImageUrl(brand.logoUrl, { width: 40, height: 40 })}
                        alt=""
                        width={40}
                        height={40}
                        className="size-10 shrink-0 rounded-m border border-border-secondary
                            object-cover"
                    />
                )}

                <h2 className="text-h4 md:text-h3">
                    {t('restaurants.detail.brandLocations.title', {
                        brandName: brand.name,
                        cityName,
                    })}
                </h2>
            </div>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
                {sortedSiblings.slice(0, MAX_SIBLING_LINKS).map((sibling) => (
                    <li
                        key={sibling.slug}
                        className="border-b border-b-border-secondary last:border-b-0
                            md:[&:nth-last-child(-n+2)]:border-b-0"
                    >
                        <Link
                            href={getLinkHref(
                                {
                                    pattern: '/restaurants/[citySlug]/[restaurantSlug]',
                                    params: {
                                        citySlug: resolvedCitySlug,
                                        restaurantSlug: sibling.slug,
                                    },
                                },
                                locale
                            )}
                            className="flex items-center justify-between gap-x-3 py-3 px-3 -mx-3
                                transition-colors duration-150 hover:bg-bg-neutral-surface
                                webkit-tap-transparent"
                        >
                            <span className="flex min-w-0 flex-col">
                                <span className="text-label-lg">{sibling.name}</span>

                                <span
                                    className="mt-0.5 flex items-center gap-x-2 text-p-sm
                                        text-fg-tertiary"
                                >
                                    {Boolean(sibling.avgRating) && (
                                        <Rating
                                            withBrackets
                                            starsStyleType="minimal"
                                            rating={sibling.avgRating}
                                            ratingCount={sibling.ratingsCount}
                                            ratingCountStyleType="only-count"
                                            starClassName="w-3 h-3"
                                            ratingClassName="text-p-sm"
                                            labelClassName="text-p-sm"
                                            labelAndStarsContainerClassName="gap-x-1"
                                            containerClassName="gap-x-0.5 items-center"
                                        />
                                    )}

                                    {Boolean(sibling.avgRating) && sibling.neighborhood && (
                                        <Middot />
                                    )}

                                    {sibling.neighborhood && <span>{sibling.neighborhood}</span>}
                                </span>
                            </span>

                            <ChevronRight className="size-[18px] shrink-0 text-fg-disabled" />
                        </Link>
                    </li>
                ))}
            </ul>

            <Link
                href={brandHref}
                className="mt-4 text-p-xs md:text-p-sm text-fg-tertiary underline
                    w-fit py-1 min-h-8 flex items-center hover:text-fg-primary transition-colors"
            >
                {t('restaurants.detail.brandLocations.showAll', {
                    brandName: brand.name,
                    cityName,
                    count: brand.locationCount,
                })}
            </Link>
        </section>
    )
}
